import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { CloseIcon } from './Icons';
import SelectMenu from './SelectMenu';

// Mirrors apps.order_query.models.OrderQuery.QUERY_TYPE_CHOICES on the ERP -
// keep in sync if that list ever changes. Exported so the Order Queries page
// (OrderQueries.js) can render the same type labels in its results table.
export const QUERY_TYPE_OPTIONS = [
  { value: 'rto', label: 'RTO' },
  { value: 'ndr', label: 'NDR' },
  { value: 'order_cancellation', label: 'Order Cancellation' },
  { value: 'order_modification', label: 'Order Modification' },
  { value: 'delivery_issue', label: 'Delivery Issue' },
  { value: 'address_issue', label: 'Address Issue' },
  { value: 'refund', label: 'Refund' },
  { value: 'replacement', label: 'Replacement' },
  { value: 'payment_issue', label: 'Payment Issue' },
  { value: 'other', label: 'Other' },
];

// How long to wait after the agent stops typing an order number/mobile
// before looking up existing queries - avoids firing on every keystroke.
const LOOKUP_DEBOUNCE_MS = 400;

// Mirrors ORDER_QUERY_MAX_ATTACHMENT_BYTES in server/ivr/routes.js - checked
// here too so an oversized file is rejected instantly instead of only after
// a slow upload attempt.
const MAX_ATTACHMENT_BYTES = 75 * 1024 * 1024;

function formatFileSize(bytes) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.ceil(bytes / 1024)} KB`;
}

function isLikelyUrl(value) {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export const STATUS_BADGE_CLASS = {
  new: 'status new',
  open: 'status viewing',
  reopened: 'status critical',
  marked_done: 'status attempted',
  resolved: 'status answered',
  rejected: 'status missed',
};

function formatDate(raw) {
  if (!raw) return '-';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function ExistingQueryRow({ q }) {
  return (
    <div className="order-query-existing-row">
      <div className="order-query-existing-row-top">
        <span className={STATUS_BADGE_CLASS[q.status] || 'status'}>{q.status_label}</span>
        <span className="order-query-existing-meta">{q.query_type_label} · {formatDate(q.created_at)}</span>
      </div>
      <div className="order-query-existing-text">{q.query}</div>
    </div>
  );
}

// Raise (or reopen) an order query directly in the ERP - see
// server/ivr/routes.js's /erp/order-queries and server/ivr/erpClient.js for
// the server side. Shows existing queries for the order number/customer
// first (always a live fetch, never stored here - same principle as
// IncomingCallPopup/DailyOrders), then lets the agent file a new one. If the
// ERP reports the order number already has an active query, or a resolved
// one it can reopen, this shows that prompt in place rather than a second
// popup - same UX the ERP's own frontend uses for the same flow.
// Strips everything but digits and keeps the trailing 10 - mirrors
// apps.crm_integration.phone_utils.normalize_phone on the ERP, so a
// prefilled number carrying a +91/spaces/dashes (e.g. from the incoming-call
// popup) lands as a clean 10-digit value instead of failing the mobile
// number's exactly-10-digits submit check untouched.
function normalizeMobile(raw) {
  const digits = (raw || '').replace(/\D/g, '');
  return digits.slice(-10);
}

function OrderQueryForm({ orderNumber: initialOrderNumber = '', customerName = '', customerMobile = '', onClose }) {
  const { authFetch } = useAuth();
  const [orderNumber, setOrderNumber] = useState(initialOrderNumber);
  const [queryType, setQueryType] = useState('');
  const [query, setQuery] = useState('');
  const [name, setName] = useState(customerName);
  const [mobile, setMobile] = useState(() => normalizeMobile(customerMobile));
  const [attachment, setAttachment] = useState(null); // File
  const [attachmentError, setAttachmentError] = useState('');
  // Caches the last successful upload for the currently-picked file, so
  // retrying submit() after a needs_confirmation/active_duplicate prompt
  // (Reopen/Create New) doesn't re-upload the same (possibly large) file.
  const [uploadedAttachment, setUploadedAttachment] = useState(null); // { file, url, name }
  // A local, in-browser object URL for the picked-but-not-yet-uploaded file,
  // so "View" works instantly without waiting on the upload - revoked
  // whenever the picked file changes or the form unmounts.
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState('');
  // Alternative to uploading a file - e.g. a link to media already hosted
  // elsewhere (Drive, WhatsApp media, etc).
  const [attachmentLink, setAttachmentLink] = useState('');
  const [attachmentLinkError, setAttachmentLinkError] = useState('');

  const [existing, setExisting] = useState([]);
  const [existingLoading, setExistingLoading] = useState(false);
  const [existingError, setExistingError] = useState('');

  const [status, setStatus] = useState('idle'); // idle | saving | done
  const [error, setError] = useState('');
  // Set when the ERP reports the order number already has an active query
  // (can't be created, only viewed) or a resolved one (can be reopened or
  // left alone in favor of a fresh entry) - see the three-shape response
  // documented on erpClient.createOrderQuery.
  const [prompt, setPrompt] = useState(null); // { kind: 'active_duplicate' | 'needs_confirmation', existingQuery }
  const [savedQuery, setSavedQuery] = useState(null);

  useEffect(() => {
    const trimmedOrder = orderNumber.trim();
    const trimmedMobile = mobile.trim();
    if (!trimmedOrder && !trimmedMobile) {
      setExisting([]);
      setExistingError('');
      setExistingLoading(false);
      return undefined;
    }

    setExistingLoading(true);
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (trimmedOrder) params.set('order_number', trimmedOrder);
        if (trimmedMobile) params.set('phone', trimmedMobile);
        const response = await authFetch(`/api/ivr/erp/order-queries?${params.toString()}`);
        const body = await response.json();
        if (!response.ok) throw new Error(body?.error || 'Failed to look up existing queries');
        // The route normalizes to { count, results }, but tolerate a bare
        // array too in case this ever talks to an older route/ERP build.
        setExisting(Array.isArray(body?.results) ? body.results : Array.isArray(body) ? body : []);
        setExistingError('');
      } catch (err) {
        setExisting([]);
        setExistingError(err.message || 'Failed to look up existing queries');
      } finally {
        setExistingLoading(false);
      }
    }, LOOKUP_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [orderNumber, mobile, authFetch]);

  // Keeps a live object URL for whichever file is currently picked, for the
  // "View" button - revoked on every change/unmount so picking several files
  // in a row doesn't leak blob URLs.
  useEffect(() => {
    if (!attachment) {
      setAttachmentPreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(attachment);
    setAttachmentPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [attachment]);

  // Validates the picked file client-side (type + the same 75 MB cap the
  // upload endpoint enforces) so a bad pick is caught instantly instead of
  // only after a slow upload attempt fails.
  const handleAttachmentChange = (event) => {
    const picked = event.target.files?.[0] || null;
    event.target.value = ''; // let the same file be re-picked after a "Remove"
    if (!picked) return;
    if (
      !picked.type.startsWith('image/')
      && !picked.type.startsWith('video/')
      && !picked.type.startsWith('audio/')
    ) {
      setAttachmentError('Attachment must be an image, video, or audio file');
      return;
    }
    if (picked.size > MAX_ATTACHMENT_BYTES) {
      setAttachmentError(`File is too large (${formatFileSize(picked.size)}) — the limit is 75 MB`);
      return;
    }
    setAttachmentError('');
    setAttachment(picked);
    setUploadedAttachment(null);
  };

  const handleAttachmentLinkChange = (event) => {
    const value = event.target.value;
    setAttachmentLink(value);
    setAttachmentLinkError(value.trim() && !isLikelyUrl(value.trim()) ? 'Enter a valid http(s) link' : '');
  };

  const submit = async (reopenDecision) => {
    if ((!orderNumber.trim() && !mobile.trim()) || !queryType || !query.trim()) {
      setError('Order number or customer mobile number, plus query type and query, are required');
      return;
    }
    if (/^\d{10}$/.test(orderNumber.trim())) {
      setError('That looks like a mobile number — please put it in the Customer Mobile Number field instead');
      return;
    }
    if (mobile.trim() && mobile.trim().length !== 10) {
      setError('Customer mobile number must be exactly 10 digits');
      return;
    }
    if (attachmentError) {
      setError(attachmentError);
      return;
    }
    if (attachmentLinkError) {
      setError(attachmentLinkError);
      return;
    }
    setStatus('saving');
    setError('');
    try {
      let attachmentUrl = uploadedAttachment?.url;
      let attachmentName = uploadedAttachment?.name;
      if (attachment && uploadedAttachment?.file !== attachment) {
        const formData = new FormData();
        formData.append('file', attachment);
        const uploadResponse = await authFetch('/api/ivr/order-queries/attachment', {
          method: 'POST',
          body: formData,
        });
        const uploadBody = await uploadResponse.json();
        if (!uploadResponse.ok) throw new Error(uploadBody?.error || 'Attachment upload failed');
        attachmentUrl = uploadBody.url;
        attachmentName = uploadBody.name;
        setUploadedAttachment({ file: attachment, url: attachmentUrl, name: attachmentName });
      }

      const response = await authFetch('/api/ivr/erp/order-queries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_number: orderNumber.trim(),
          query: query.trim(),
          query_type: queryType,
          customer_name: name.trim(),
          customer_mobile: mobile.trim(),
          reopen_decision: reopenDecision,
          attachment_url: attachmentUrl,
          attachment_name: attachmentName,
          attachment_link: attachmentLink.trim() || undefined,
        }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body?.error || 'Failed to submit query');

      if (body?.active_duplicate) {
        setPrompt({ kind: 'active_duplicate', existingQuery: body.existing_query });
        setStatus('idle');
        return;
      }
      if (body?.needs_confirmation) {
        setPrompt({ kind: 'needs_confirmation', existingQuery: body.existing_query });
        setStatus('idle');
        return;
      }

      setPrompt(null);
      setSavedQuery(body.query);
      setStatus('done');
    } catch (err) {
      setError(err.message || 'Failed to submit query');
      setStatus('idle');
    }
  };

  return (
    <div className="modal-overlay modal-overlay-center" onClick={onClose}>
      <div className="modal-panel modal-panel-narrow" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Raise Order Query</h2>
            <p className="modal-subtitle">Files a query directly in the ERP for this order.</p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <CloseIcon width={18} height={18} />
          </button>
        </div>

        <div className="modal-body">
          {status !== 'done' && !prompt && (existingLoading || existing.length > 0 || existingError) && (
            <div className="order-query-existing-list">
              <div className="order-query-existing-title">
                {existingLoading ? 'Checking existing queries…' : 'Existing queries for this order/customer'}
              </div>
              {existingError && <div className="call-feedback call-feedback-error">{existingError}</div>}
              {existing.map((q) => (
                <ExistingQueryRow key={q.id} q={q} />
              ))}
            </div>
          )}

          {status === 'done' ? (
            <>
              <p className="call-feedback call-feedback-ok">
                Query saved for order {savedQuery?.order_number} ({savedQuery?.status_label}).
              </p>
              <div className="follow-up-add-actions">
                <button type="button" className="btn btn-secondary" onClick={onClose}>
                  Close
                </button>
              </div>
            </>
          ) : prompt ? (
            <div className="order-query-prompt">
              <p>
                {prompt.existingQuery.order_number
                  ? `Order #${prompt.existingQuery.order_number}`
                  : `Mobile ${prompt.existingQuery.customer_mobile}`}{' '}
                already has {prompt.kind === 'active_duplicate' ? 'an active' : 'a resolved'} query.
              </p>
              <ExistingQueryRow q={prompt.existingQuery} />
              <div className="follow-up-add-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setPrompt(null)}>
                  {prompt.kind === 'active_duplicate' ? 'Close' : 'Dismiss'}
                </button>
                {prompt.kind === 'needs_confirmation' && (
                  <>
                    {/* A blank order_number on the matched query means it was matched by
                        mobile number, not order number (see the ERP's services.py dedup_filter)
                        - the customer could have a genuinely separate issue, so offer
                        "Create New" alongside "Reopen". A same-order-number match is the
                        same ticket, so reopening is the only sensible choice. */}
                    {!prompt.existingQuery.order_number && (
                      <button type="button" className="btn btn-secondary" disabled={status === 'saving'} onClick={() => submit('new')}>
                        Create New
                      </button>
                    )}
                    <button type="button" className="btn btn-primary" disabled={status === 'saving'} onClick={() => submit('reopen')}>
                      {status === 'saving' ? 'Saving…' : 'Reopen'}
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                submit(undefined);
              }}
            >
              <label className="login-field">
                <span>Order Number (or provide mobile number below)</span>
                <input value={orderNumber} onChange={(event) => setOrderNumber(event.target.value)} autoFocus />
              </label>
              <label className="login-field">
                <span>Customer Mobile Number</span>
                <input
                  value={mobile}
                  onChange={(event) => setMobile(event.target.value.replace(/\D/g, '').slice(0, 10))}
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="e.g. 9876543210"
                />
              </label>
              <label className="login-field">
                <span>Customer Name</span>
                <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Optional" />
              </label>
              <label className="login-field">
                <span>Query Type</span>
                <SelectMenu
                  value={queryType}
                  onChange={setQueryType}
                  ariaLabel="Query Type"
                  options={[{ value: '', label: 'Select a query type…' }, ...QUERY_TYPE_OPTIONS]}
                />
              </label>
              <label className="login-field">
                <span>Query</span>
                <textarea
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Describe the issue with this order"
                  rows={3}
                />
              </label>
              <label className="login-field">
                <span>Attachment (optional, image, video, or audio, up to 75 MB)</span>
                <input type="file" accept="image/*,video/*,audio/*,.mp3" onChange={handleAttachmentChange} />
              </label>
              {attachment && (
                <div className="order-query-attachment-preview">
                  <span>{attachment.name} ({formatFileSize(attachment.size)})</span>
                  <span className="order-query-attachment-preview-actions">
                    {attachmentPreviewUrl && (
                      <a
                        href={attachmentPreviewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary btn-small"
                      >
                        View
                      </a>
                    )}
                    <button
                      type="button"
                      className="btn btn-secondary btn-small"
                      onClick={() => {
                        setAttachment(null);
                        setAttachmentError('');
                        setUploadedAttachment(null);
                      }}
                    >
                      Remove
                    </button>
                  </span>
                </div>
              )}
              {attachmentError && <div className="login-error">{attachmentError}</div>}

              <label className="login-field">
                <span>Or attach a link (optional)</span>
                <div className="order-query-attachment-link-row">
                  <input
                    type="url"
                    value={attachmentLink}
                    onChange={handleAttachmentLinkChange}
                    placeholder="https://…"
                  />
                  {attachmentLink.trim() && !attachmentLinkError && (
                    <a
                      href={attachmentLink.trim()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary btn-small"
                    >
                      View
                    </a>
                  )}
                </div>
              </label>
              {attachmentLinkError && <div className="login-error">{attachmentLinkError}</div>}

              {error && <div className="login-error">{error}</div>}

              <div className="follow-up-add-actions">
                <button type="button" className="btn btn-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={status === 'saving'}>
                  {status === 'saving' ? (attachment ? 'Uploading…' : 'Saving…') : 'Raise Query'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrderQueryForm;
